import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardDescription, CardTitle } from '@/components/ui/card';
import { UniversalCombobox, UniversalComboboxBillingPeriod, DateTimePicker } from '@/components/common';
import { currencyLabel } from '@/lib/utils/currency';
import { useReportFilters, useContractClientReport, useReportTotals } from '../hooks';
import { useContractData } from './useContractData';
import { ReportTasksTable } from '../components';

interface SelectedEntity {
  id: number;
  name: string;
  type?: string;
  currency?: string;
  dolarRate?: number;
  hourlyPackageId?: number;
  contactFace?: string;
  createdAt?: string;
}

function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

export function ReportsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedClient, setSelectedClient] = useState<SelectedEntity | null>(null);

  const { filters, updateFilter } = useReportFilters();
  const { data: tasks, refetch } = useContractClientReport(filters);

  // Contract + package are derived from the URL rather than from the combobox
  // callback, so a shared or refreshed link hydrates the same as a click-through.
  // Previously they stayed null on a direct load, which blanked the package cap
  // and fell back to a dollar label on a UZS contract.
  const { selectedContract, hourlyPackage } = useContractData(
    filters.contractId ? String(filters.contractId) : undefined,
  );

  const handleClientFormatClick = () => {
    const contractId = selectedContract?.id ?? filters.contractId;
    if (!contractId) return;
    // Carry the whole filter set into the client format. Dropping it silently
    // produced a "whole period" document — every task of the contract — where the
    // user had asked for one period. Defect AL-2026/08-03-01, D-04.
    const params = new URLSearchParams();
    if (selectedClient?.name) params.set('clientName', selectedClient.name);
    if (filters.billingPeriodId) params.set('billingPeriodId', String(filters.billingPeriodId));
    if (filters.matterId) params.set('matterId', String(filters.matterId));
    if (filters['endedAt.min']) params.set('endedAt.min', filters['endedAt.min']);
    if (filters['endedAt.max']) params.set('endedAt.max', filters['endedAt.max']);
    navigate(`/app/reports/client-format/${contractId}?${params.toString()}`);
  };

  // Every figure on this screen comes from the shared hook, so the client-format
  // document cannot print a different number for the same contract.
  const totals = useReportTotals({
    tasks,
    contract: selectedContract,
    hourlyPackage,
    contractId: filters.contractId,
    billingPeriodId: filters.billingPeriodId,
    min: filters['endedAt.min'],
    max: filters['endedAt.max'],
  });

  const {
    taskCount,
    tasksDone,
    totalDuration,
    capSeconds,
    capPrice: capPriceInContractCurrency,
    displayedBilled: displayedTotalBilled,
    displayedBilledPrice: displayedTotalBilledPrice,
    isOverBilled,
    overtimeSeconds,
  } = totals;

  const capHours = capSeconds / 3600;
  const contractCurrencyLabel = currencyLabel(selectedContract?.currency);
  // Billed price column is always visible — prices on ACTIVE-package tasks may
  // still change as more activity lands, but the user wants the column shown
  // for every contract regardless of package state.
  const showBilledPrice = true;

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b">
        <h1 className="text-xl font-semibold">{t('reports')}</h1>
      </div>
      <div className="px-4 py-2">
        <div className="w-full">
          {/* Filters */}
          <div className="flex flex-wrap items-center justify-start gap-2 py-4">
            {/* End-date range — filters report tasks by their end date. Two
                separate start/end date selects. */}
            <DateTimePicker
              mode="date"
              value={filters['endedAt.min'] ?? undefined}
              onChange={(date) =>
                updateFilter('endedAt.min', date ? date.toISOString() : undefined)
              }
              maxDate={filters['endedAt.max'] ? new Date(filters['endedAt.max']) : undefined}
              placeholder={t('startDate')}
              className="w-[180px]"
            />
            <DateTimePicker
              mode="date"
              value={filters['endedAt.max'] ?? undefined}
              onChange={(date) =>
                updateFilter('endedAt.max', date ? date.toISOString() : undefined)
              }
              minDate={filters['endedAt.min'] ? new Date(filters['endedAt.min']) : undefined}
              placeholder={t('endDate')}
              className="w-[180px]"
            />

            {/* Client Select */}
            <UniversalCombobox
              url="/clients"
              labelKey="name"
              labelKeys={['lastname', 'name', 'middlename']}
              valueKey="id"
              selectedId={filters.clientId ?? undefined}
              onSelectItem={(client) => {
                updateFilter('clientId', client?.id);
                setSelectedClient(client || null);
              }}
              placeholder={t('selectClient')}
              className="w-[200px]"
            />

            {/* Contract Select */}
            <UniversalCombobox
              url="/contracts"
              listUrl="/contracts/select-box"
              labelKey="name"
              valueKey="id"
              selectedId={filters.contractId ?? undefined}
              onSelectItem={(contract) => {
                // The URL is the single source of truth; `useContractData` picks
                // the change up and loads the contract + its package.
                updateFilter('contractId', contract?.id);
              }}
              placeholder={t('selectContract')}
              params={{ clientId: filters.clientId }}
              className="w-[200px]"
            />

            {/* Matter Select */}
            <UniversalCombobox
              url="/matters"
              listUrl="/matters/select-box"
              labelKey="name"
              valueKey="id"
              selectedId={filters.matterId ?? undefined}
              onSelectItem={(matter) => {
                updateFilter('matterId', matter?.id);
              }}
              placeholder={t('selectMatter')}
              params={{ contractId: filters.contractId }}
              className="w-[200px]"
            />

            {/* Billing Period Select */}
            <UniversalComboboxBillingPeriod
              url="/billing-periods"
              valueKey="id"
              labelKey="startDate"
              selectedId={filters.billingPeriodId ?? undefined}
              disabled={!filters.contractId}
              onSelectItem={(period) => {
                updateFilter('billingPeriodId', period?.id || undefined);
              }}
              placeholder={t('selectPeriod')}
              params={{ contractId: filters.contractId }}
              className="w-[300px]"
            />

            {/* Client Format Button */}
            <Button
              disabled={!filters.contractId}
              className="ml-auto"
              onClick={handleClientFormatClick}
            >
              {t('clientFormat')}
            </Button>
          </div>

          {/* Report data — shown once a contract is selected. A billing period
              is optional; the end-date range narrows the tasks further. */}
          {filters.contractId ? (
            <>
              {/* Stats Cards */}
              <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-5 gap-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs mb-3 @xl/main:grid-cols-2 @5xl/main:grid-cols-4">
                <Card className="@container/card">
                  <CardHeader>
                    <CardDescription>{t('taskCount')}</CardDescription>
                    <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
                      {taskCount}
                    </CardTitle>
                  </CardHeader>
                </Card>

                <Card className="@container/card">
                  <CardHeader>
                    <CardDescription>{t('taskDone')}</CardDescription>
                    <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
                      {tasksDone}
                    </CardTitle>
                  </CardHeader>
                </Card>

                <Card className="@container/card">
                  <CardHeader>
                    <CardDescription>{t('totalDuration')}</CardDescription>
                    <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
                      {formatTime(totalDuration)}
                    </CardTitle>
                  </CardHeader>
                </Card>

                <Card className="@container/card">
                  <CardHeader>
                    <CardDescription>{t('totalBilled')}</CardDescription>
                    <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
                      {formatTime(displayedTotalBilled)}
                      <span className="ml-2 text-sm text-muted-foreground">
                        / {capHours.toLocaleString()}h
                      </span>
                      {isOverBilled && (
                        <span className="ml-2 text-sm text-red-600">
                          ({formatTime(overtimeSeconds)})
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                </Card>

                {showBilledPrice && (
                  <Card className="@container/card">
                    <CardHeader>
                      <CardDescription>{t('totalBilledPrice')}</CardDescription>
                      <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
                        {displayedTotalBilledPrice.toLocaleString()} {contractCurrencyLabel}
                        <span className="ml-2 text-sm text-muted-foreground">
                          / {capPriceInContractCurrency.toLocaleString()} {contractCurrencyLabel}
                        </span>
                      </CardTitle>
                    </CardHeader>
                  </Card>
                )}
              </div>

              {/* Tasks Table */}
              <div className="border rounded-md">
                <ReportTasksTable
                  tasks={tasks || []}
                  selectedClient={selectedClient}
                  selectedContract={selectedContract}
                  refetch={refetch}
                  showBilledPrice={showBilledPrice}
                />
              </div>
            </>
          ) : (
            <div className="border rounded-md py-12 text-center text-muted-foreground">
              {t('selectContractToViewReport')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
